import {
  AllPointOfInterestsForDataLoaderQuery,
  PointOfInterest,
  UnitsEnum,
  UnitSystemEnum,
  useAllPointOfInterestsForDataLoaderQuery,
  ReportOperationalFields,
  useStructureForReportQuery,
  ReviewActionChecklistConfig,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { checkIfVoi } from '@/utils/poi';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { normalizeReviewActionValue } from '@/utils/review-action-helpers';
import { useSelectedAssemblyTemplateId } from '../../hooks';
import { useViewPoiIn3D } from '../../Insights/InsightsExplorer/PointOfInterests/useViewPoiIn3D';

const getSCCategoryToDisplay = (
  templateFieldData: Record<string, unknown> | null | undefined
): string | null => {
  if (!templateFieldData) return null;

  const scCategories = new Set(['SC0', 'SC1', 'SC2', 'SC3', 'SC4', 'SC5', 'N/A']);
  const templateFieldDataKeys = Object.keys(templateFieldData);
  for (const key of templateFieldDataKeys) {
    if (
      templateFieldData[key] &&
      typeof templateFieldData[key] === 'string' &&
      scCategories.has(templateFieldData[key])
    ) {
      return templateFieldData[key];
    }
  }
  return null;
};

const extractPoiFieldValue = (
  poi: PointOfInterest,
  fieldKey: string,
  unitSystem: UnitSystemEnum
): string => {
  if (fieldKey === 'sc_category' || fieldKey === 'scCategory') {
    return getSCCategoryToDisplay(poi?.templateFieldData) ?? NOT_APPLICABLE_LABEL;
  }

  const poiRecord = poi as unknown as Record<string, unknown>;

  // Special handling: height_from_deck -> deckRelativeHeight (needs measurement formatting)
  if (fieldKey === 'deck_relative_height' && 'deckRelativeHeight' in poiRecord) {
    const value = poiRecord.deckRelativeHeight;
    if (value !== null && value !== undefined) {
      return (
        localiseMeasurement({
          unitSystem,
          value: value as number,
          sourceUnit: UnitsEnum.Meter,
          roundToDecimal: 1,
          fallbackLabel: NOT_APPLICABLE_LABEL,
        }) ?? NOT_APPLICABLE_LABEL
      );
    }
  }

  if (fieldKey === 'paint_region' && 'paintRegion' in poiRecord) {
    const paintRegion = poiRecord.paintRegion as { name?: string } | null | undefined;
    return paintRegion?.name ?? NOT_APPLICABLE_LABEL;
  }

  if (fieldKey === 'poi_type' && 'type' in poiRecord) {
    const value = poiRecord.type;
    return value !== null && value !== undefined ? String(value) : NOT_APPLICABLE_LABEL;
  }

  if (fieldKey === 'poi_id') {
    const value = poiRecord.poiId;
    return value !== null && value !== undefined ? String(value) : NOT_APPLICABLE_LABEL;
  }

  if (fieldKey in poiRecord) {
    const value = poiRecord[fieldKey];
    if (value !== null && value !== undefined) {
      if (fieldKey === 'deckRelativeHeight') {
        return (
          localiseMeasurement({
            unitSystem,
            value: value as number,
            sourceUnit: UnitsEnum.Meter,
            roundToDecimal: 1,
            fallbackLabel: NOT_APPLICABLE_LABEL,
          }) ?? NOT_APPLICABLE_LABEL
        );
      }
      if (fieldKey === 'paintRegion') {
        const paintRegion = value as { name?: string } | null | undefined;
        return paintRegion?.name ?? NOT_APPLICABLE_LABEL;
      }
      return String(value);
    }
  }

  if (poi.templateFieldData && typeof poi.templateFieldData === 'object') {
    const templateData = poi.templateFieldData as Record<string, unknown>;
    if (fieldKey in templateData) {
      const value = templateData[fieldKey];
      return value !== null && value !== undefined ? String(value) : NOT_APPLICABLE_LABEL;
    }
  }

  if (fieldKey in (poi ?? {})) {
    const value = poi?.[fieldKey as keyof PointOfInterest];
    return value !== null ? String(value) : NOT_APPLICABLE_LABEL;
  }

  return NOT_APPLICABLE_LABEL;
};

type ReviewActionPoiGroup = {
  title: string;
  reviewAction: string;
  data: Array<
    Record<
      string,
      {
        value?: string;
        onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
        displayConfig?: { type: string };
      }
    >
  >;
  headers?: string[];
};

const getPoiId = (poi: { id?: string; _id?: string }): string =>
  poi?.id !== null ? String(poi.id) : poi?._id != null ? String(poi._id) : '';

/**
 * Filters review action checklist config to only include items that are enabled and have corresponding POIs
 */
export function filterReviewActionsWithPois<
  T extends { reviewActionValue: string; isEnabled: boolean; position?: number }
>(checklistConfig: T[], reviewActionPois: Record<string, unknown[]> | undefined): T[] {
  const normalizedPoisMap = new Map<string, string>();
  if (reviewActionPois && typeof reviewActionPois === 'object') {
    for (const [key, pois] of Object.entries(reviewActionPois)) {
      if (Array.isArray(pois) && pois.length > 0) {
        const normalizedKey = normalizeReviewActionValue(key);
        normalizedPoisMap.set(normalizedKey, key);
      }
    }
  }

  const enabledConfigs = checklistConfig.filter((config) => config.isEnabled);
  const configsWithPois: T[] = [];
  for (const config of enabledConfigs) {
    const reviewActionValue = config.reviewActionValue.trim();
    const normalizedConfigKey = normalizeReviewActionValue(reviewActionValue);

    const hasPois =
      normalizedPoisMap.has(normalizedConfigKey) ||
      (reviewActionPois &&
        reviewActionPois[reviewActionValue] &&
        Array.isArray(reviewActionPois[reviewActionValue]) &&
        reviewActionPois[reviewActionValue].length > 0);

    // Step 4: Only include if POIs exist
    if (hasPois) {
      configsWithPois.push(config);
    }
  }

  // Sort by position
  configsWithPois.sort((a, b) => (a.position || 0) - (b.position || 0));

  return configsWithPois;
}

export const useFilterReviewActionPois = (assemblyId?: string) => {
  const structureId = useGetStructureId();
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const allVoiTemplates = useAtomValue(state.allVoiTemplates);
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const report = useAtomValue(state.selectedReport);
  const assemblyTemplateId = useSelectedAssemblyTemplateId();
  const { createClickHandler } = useViewPoiIn3D();

  const { data: structureData } = useStructureForReportQuery({
    variables: { structureId: structureId ?? '' },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const getConfigForReviewAction = useCallback(
    (reviewAction: string): ReportOperationalFields[] => {
      const reportConfig = structureData?.structure?.reportConfig as
        | { reviewActionTableConfigs?: Record<string, { fields?: ReportOperationalFields[] }> }
        | null
        | undefined;
      const reviewActionTableConfigs = reportConfig?.reviewActionTableConfigs;
      return reviewActionTableConfigs?.[reviewAction]?.fields ?? [];
    },
    [structureData?.structure?.reportConfig]
  );

  const { loading } = useAllPointOfInterestsForDataLoaderQuery({
    variables: {
      input: {
        structureId,
        assemblyIds: [assemblyId ?? selectedAssemblyId ?? ''],
        assemblyTemplateId: assemblyTemplateId,
      },
    },
    skip: !assemblyId && !selectedAssemblyId,
  });

  const checklistConfig = useMemo(() => {
    if (!report?.components || report.components.length === 0) {
      return [] as ReviewActionChecklistConfig[];
    }

    const firstComponent = report.components[0] as {
      template?: { reviewActionChecklistConfig?: ReviewActionChecklistConfig[] };
    };
    const template = firstComponent?.template;
    const config = (template?.reviewActionChecklistConfig || []) as ReviewActionChecklistConfig[];

    return config;
  }, [report]);

  const reviewActionPois = useMemo(() => {
    const reportWithPois = report as {
      reviewActionPois?: Record<string, PointOfInterest[]> | null;
    };
    if (!reportWithPois?.reviewActionPois || typeof reportWithPois.reviewActionPois !== 'object') {
      return {} as Record<string, PointOfInterest[]>;
    }

    return reportWithPois.reviewActionPois as Record<string, PointOfInterest[]>;
  }, [report]);

  const fullPoiById = useMemo(() => {
    const list = (report as { pointOfInterests?: PointOfInterest[] })?.pointOfInterests ?? [];
    const map = new Map<string, PointOfInterest>();
    list.forEach((poi) => {
      if (poi?.id) map.set(String(poi.id), poi);
      if (poi && '_id' in poi && (poi as { _id?: string })._id !== null) {
        map.set(String((poi as { _id: string })._id), poi);
      }
    });
    return map;
  }, [report]);

  const createHandleViewIn3DMap = useCallback(
    (pois: AllPointOfInterestsForDataLoaderQuery['pointOfInterests']) => {
      const map = new Map<string, (event: React.MouseEvent<HTMLButtonElement>) => void>();
      pois.forEach((poi) => {
        const poiId = getPoiId(poi as { id?: string; _id?: string });
        const fullPoi = (fullPoiById.get(poiId) ?? poi) as PointOfInterest;
        if (!fullPoi?.assembly) {
          return;
        }
        const { x, y, z } = fullPoi.centerPoint3d ?? {};
        const centerPoint3dExists = ![x, y, z].includes(undefined);
        const isUncontextualized = !centerPoint3dExists;
        const isVoi = checkIfVoi(allVoiTemplates, fullPoi.type);
        const imageViewExists = fullPoi.location?.name;

        let imageLink = NOT_APPLICABLE_LABEL;
        let fov = 0;

        if (!isUncontextualized) {
          fov = isVoi ? 0 : imageViewExists ? 0 : 2;
          const lookAt = `&look.x=${x}&look.y=${y}&look.z=${z}`;
          const position = `&position.x=${imageViewExists ? x : 0}&position.y=${
            imageViewExists ? y : 0
          }&position.z=${imageViewExists ? z : 0}`;
          const idForLink = fullPoi.id ?? (fullPoi as { _id?: string })._id ?? poiId;
          imageLink = `${window.location.origin}/analysis/${structureId}/viewer?assembly=${fullPoi.assembly.tagName}&poi=${idForLink}${lookAt}${position}&fov=${fov}&showOnlySelectedPoi=true`;
          if (imageViewExists) imageLink += `&selectedSpherical=${fullPoi.location?.name}`;
        }

        const handler = createClickHandler({
          assemblyId: fullPoi.assembly.id,
          isUncontextualized,
          centerPoint3d: centerPoint3dExists ? JSON.stringify([x, y, z]) : '',
          imageLink,
          fov,
        });
        map.set(poiId, handler);
      });
      return map;
    },
    [structureId, allVoiTemplates, createClickHandler, fullPoiById]
  );

  const transformPois = useCallback(
    (
      pois: AllPointOfInterestsForDataLoaderQuery['pointOfInterests'],
      handleViewIn3DMap: Map<string, (event: React.MouseEvent<HTMLButtonElement>) => void>,
      reviewAction: string
    ) => {
      const configFields = getConfigForReviewAction(reviewAction);
      const visibleFields = configFields.filter(
        (field) => field && field.visibility !== false && field.key
      );
      const hasConfig = configFields.length > 0;
      const fieldsToProcess = hasConfig
        ? visibleFields
        : [
            { key: 'poi_id', title: 'POI#', visibility: true },
            { key: 'poi_type', title: 'POI Type', visibility: true },
            { key: 'paint_region', title: 'Paint Region', visibility: true },
          ];
      return pois.map((poi) => {
        const poiId = getPoiId(poi as { id?: string; _id?: string });
        const handleViewIn3D = handleViewIn3DMap.get(poiId);
        const rowData: Record<
          string,
          {
            value?: string;
            onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
            displayConfig?: { type: string };
            isLink?: boolean;
          }
        > = {};

        // Add all configured fields dynamically
        for (const fieldConfig of fieldsToProcess.slice(0, 5)) {
          if (!fieldConfig.key) continue;
          const fieldTitle = fieldConfig.title || fieldConfig.key;
          const fieldValue = extractPoiFieldValue(
            poi as PointOfInterest,
            fieldConfig.key,
            unitConversionSystem
          );
          const lowerKey = (fieldConfig.key || '').toLowerCase();
          const lowerTitle = String(fieldTitle).toLowerCase();
          const isLink = lowerKey.includes('link') || lowerTitle.includes('link');
          rowData[fieldTitle] = { value: fieldValue, isLink };
        }

        // Always add View 3D button
        rowData['View 3D'] = {
          onClick: handleViewIn3D,
          displayConfig: {
            type: '3DBUTTON',
          },
        };

        return rowData;
      });
    },
    [unitConversionSystem, getConfigForReviewAction]
  );

  const allPois = useMemo(() => {
    const groups: ReviewActionPoiGroup[] = [];
    const configsWithPois = filterReviewActionsWithPois(checklistConfig, reviewActionPois);
    const targetAssemblyId = assemblyId || selectedAssemblyId;

    for (const config of configsWithPois) {
      const reviewAction = config.reviewActionValue.trim();
      const key = normalizeReviewActionValue(reviewAction);
      const allPoisForAction = reviewActionPois[key] || reviewActionPois[reviewAction] || [];

      const pois = targetAssemblyId
        ? allPoisForAction.filter((poi) => {
            const poiWithAssembly = poi as PointOfInterest & { assemblyId?: string };
            const poiAssemblyId =
              poiWithAssembly.assemblyId?.toString() ?? poi.assembly?.id?.toString();
            return poiAssemblyId === targetAssemblyId;
          })
        : allPoisForAction;

      if (pois.length > 0) {
        const handleViewIn3DMap = createHandleViewIn3DMap(pois);
        // Get configured fields for this review action
        const configFields = getConfigForReviewAction(reviewAction);
        const visibleFields = configFields.filter(
          (field) => field && field.visibility !== false && field.key
        );
        const hasConfig = configFields.length > 0;
        const fieldsToProcess = hasConfig
          ? visibleFields
          : [
              { key: 'poi_id', title: 'POI#', visibility: true },
              { key: 'poi_type', title: 'POI Type', visibility: true },
              { key: 'paint_region', title: 'Paint Region', visibility: true },
            ];

        // Transform POIs to get actual data
        const transformedData = transformPois(pois, handleViewIn3DMap, reviewAction);

        // Generate headers from all configured fields (regardless of data)
        const headers = fieldsToProcess
          .slice(0, 5)
          .map((field) => field.title || field.key)
          .filter((header) => header); // Remove any empty headers

        // Always add 'View 3D' at the end
        headers.push('View 3D');

        groups.push({
          title: config.displayName,
          reviewAction: reviewAction,
          data: transformedData,
          headers: headers,
        });
      }
    }

    return groups;
  }, [
    reviewActionPois,
    checklistConfig,
    assemblyId,
    selectedAssemblyId,
    createHandleViewIn3DMap,
    transformPois,
    getConfigForReviewAction,
  ]);

  const headers = useMemo(() => {
    return Array.from(
      new Set(allPois.flatMap((group) => group.data).flatMap((row) => Object.keys(row)))
    );
  }, [allPois]);

  return {
    reviewActionPois,
    allPois,
    loading,
    headers,
  };
};
